import styled from '@emotion/styled';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import ServerError from '../../../../../components/ServerError';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import { useTableContentFiltration } from '../../../manager/components/useTableContent';
import { useManagerSkillsQuery } from '../../../__generated__/graphql';
import { mapManagerSkill } from './mappings';
import { filterByCategoryIds } from '../common/mappings';
import { ManageSkillsFilter } from './ManageSkillsFilter';
import { ManageSkillsTable } from './ManageSkillTable';
import { ManagerSkill } from './types';
import { SkillCategoryFilter } from './SkillCategoryFilter';

const FilterWrapper = styled.div({
  paddingLeft: '1rem',
  paddingRight: '1rem',
});

export const ManageSkillsConnected: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  const [filter, onFilterChange] = useTableContentFiltration();
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

  const { data, loading, error } = useManagerSkillsQuery();

  const skills: ManagerSkill[] | undefined = useMemo(
    () => data?.skills.map(mapManagerSkill),
    [data],
  );

  // Precomputed once per skill list, not on every keystroke: match the
  // canonical name and any translation so a German/French/etc. name finds the
  // skill even though the table shows the base name.
  const searchTermsById = useMemo(
    () =>
      new Map(
        skills?.map((s) => [
          s.id,
          [s.name, ...Object.values(s.translations)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        ]) ?? [],
      ),
    [skills],
  );

  const filteredSkills = useMemo(() => {
    const byCategory = skills && filterByCategoryIds(skills, categoryFilter);

    const term = filter.trim().toLowerCase();
    if (!term) {
      return byCategory;
    }
    return byCategory?.filter((s) =>
      (searchTermsById.get(s.id) ?? '').includes(term),
    );
  }, [skills, categoryFilter, filter, searchTermsById]);

  if (error) {
    return <ServerError />;
  }

  return (
    <>
      <FilterWrapper className="d-flex gap-3 align-items-center">
        <div className="flex-grow-1">
          <ManageSkillsFilter
            onFilterChange={onFilterChange}
            placeholder={t('skills.manager.filter.placeholder')}
          />
        </div>
        <SkillCategoryFilter
          value={categoryFilter}
          onChange={setCategoryFilter}
        />
      </FilterWrapper>
      {!loading && filteredSkills ? (
        <ManageSkillsTable data={filteredSkills} />
      ) : (
        <Loading />
      )}
    </>
  );
};

const Loading = () => {
  return (
    <>
      <Skeleton height={'3rem'} />
      <SkeletonTable columns={5} rows={5} />
    </>
  );
};
